import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import { Eye, EyeOff } from 'lucide-react';
import PageNarration from './PageNarration';
import LaunchBanner from './LaunchBanner';
// WhatsNewTicker import removed Apr 30 2026 iter_66 per Lead — sign-in
// page shows a static site-features list inline instead.

export default function AuthPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-[#0A0A0A] flex flex-col" data-testid="auth-page">
      <LaunchBanner />

      <div className="flex-1 flex flex-col items-center justify-center p-4 relative overflow-hidden">
        {/* Background pattern */}
        <div className="fixed inset-0 opacity-5 pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25px 25px, #2F8A92 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }} />
        </div>

      {/* Apr 30 2026 iter_67 per Lead: swap positions — sign-in box on
          LEFT, features on RIGHT. Features box gets a visible scrollbar
          so all 8 items are reachable inside the static panel. Page
          itself does NOT scroll; only the features list scrolls inside
          its own box. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 w-full max-w-5xl relative z-10 items-stretch" data-testid="auth-page-grid">
        {/* LEFT — auth form panel */}
        <div
          className="p-8 lg:p-10 rounded-md flex flex-col"
          style={{
            background: '#0A0A0A',
            border: '1px solid rgba(212,168,74,0.22)',
            boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(212,168,74,0.05)',
          }}
          data-testid="auth-form-container"
        >
        <div className="text-center mb-5">
          <img
            src="/assets/brand/tsogn-full.png"
            alt="TSOGN — The Shared Origins Global Network"
            className="mx-auto w-32 md:w-36 h-auto object-contain select-none"
            data-testid="auth-brand-logo"
          />
          <span className="sr-only" data-testid="auth-brand-tsogn">TSOGN</span>
          <span className="sr-only" data-testid="auth-brand-tagline">The Shared Origins Global Network</span>
          <p className="font-mono text-xs md:text-sm text-white/75 mt-2 leading-relaxed">
            Sources, contradictions, and the patterns history keeps repeating.
          </p>
        </div>

        {/* Toggle — Apr 30 2026 iter_66 per Lead: "Sign button blacked
            out." Inactive tab text bumped from text-[#52525B] (near-black,
            invisible on the dark panel) to text-white/70 so both LOGIN
            and REGISTER tabs are clearly readable at all times. */}
        <div className="flex mb-6 overflow-hidden rounded-sm border border-[#2F8A92]/22" data-testid="auth-toggle">
          <button
            data-testid="auth-login-tab"
            onClick={() => { setIsLogin(true); setError(''); }}
            className={`flex-1 font-hud text-sm tracking-wider py-3 transition-all duration-300 ${
              isLogin ? 'bg-[#2F8A92]/22 text-[#2F8A92]' : 'text-white/70 hover:text-white'
            }`}
          >
            LOGIN
          </button>
          <button
            data-testid="auth-register-tab"
            onClick={() => { setIsLogin(false); setError(''); }}
            className={`flex-1 font-hud text-sm tracking-wider py-3 transition-all duration-300 ${
              !isLogin ? 'bg-[#2F8A92]/22 text-[#2F8A92]' : 'text-white/70 hover:text-white'
            }`}
          >
            REGISTER
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 border border-red-400/30 bg-red-400/5 font-mono text-xs text-red-400" data-testid="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="font-hud text-[10px] text-white/80 tracking-widest uppercase block mb-1">NAME</label>
              <input
                data-testid="auth-name-input"
                className="hud-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
                required={!isLogin}
              />
            </div>
          )}
          <div>
            <label className="font-hud text-[10px] text-white/80 tracking-widest uppercase block mb-1">EMAIL</label>
            <input
              data-testid="auth-email-input"
              type="email"
              className="hud-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          <div className="relative">
            <label className="font-hud text-[10px] text-white/80 tracking-widest uppercase block mb-1">PASSWORD</label>
            <input
              data-testid="auth-password-input"
              type={showPassword ? 'text' : 'password'}
              className="hud-input pr-10"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 bottom-2 text-[#52525B] hover:text-[#2F8A92] transition-all duration-300"
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full font-hud tracking-widest mt-2 transition-all duration-300"
            style={{
              color: '#0A0A0A',
              backgroundColor: loading ? '#52525B' : '#D4A84A',
              padding: '16px 20px',
              fontSize: '1rem',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              boxShadow: '0 0 28px rgba(212,168,74,0.25)',
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#E5BC60'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#D4A84A'; }}
          >
            {loading ? 'AUTHENTICATING...' : isLogin ? 'ACCESS NETWORK' : 'JOIN NETWORK'}
          </button>
        </form>

        <p className="text-center font-mono text-xs md:text-sm text-white/75 mt-6">
          {isLogin ? 'New here? ' : 'Already have an account? '}
          <button
            data-testid="auth-switch-mode"
            onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="text-[#D4A84A] hover:text-[#E5BC60] transition-all duration-300 underline underline-offset-2"
          >
            {isLogin ? 'Register' : 'Sign in'}
          </button>
        </p>

        {isLogin && (
          <p className="text-center font-mono text-xs md:text-sm text-white/65 mt-2">
            <a
              href="/forgot-password"
              data-testid="auth-forgot-link"
              className="text-white/80 hover:text-[#D4A84A] transition-all duration-300 underline underline-offset-2"
            >
              Forgot your password?
            </a>
          </p>
        )}
        </div>

        {/* RIGHT — features panel (narrated + scrollable) */}
        <div
          className="p-8 lg:p-10 rounded-md flex flex-col"
          style={{
            background: '#0A0A0A',
            border: '1px solid rgba(212,168,74,0.22)',
            boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 60px rgba(212,168,74,0.05)',
            maxHeight: 'calc(100vh - 160px)',
          }}
          data-testid="auth-features-panel"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="font-mono text-xs md:text-sm tracking-[0.32em] uppercase text-[#D4A84A]">
              ⟨ What you get inside ⟩
            </div>
            <PageNarration pageKey="signin" label="Listen" />
          </div>
          <h2 className="font-historical text-2xl md:text-3xl lg:text-4xl text-white font-light leading-tight mb-6">
            Site features at a glance.
          </h2>
          <ul
            className="space-y-4 font-historical text-base md:text-lg text-white/85 leading-snug list-none overflow-y-auto pr-3 features-scroll"
            data-testid="auth-features-list"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#D4A84A #0A0A0A' }}
          >
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Sources for every claim.</span> Click any chip; read the citation.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Contradictions surfaced.</span> Disagreement is part of the chain, not hidden.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">AI moderates behavior, not ideas.</span> The reader stays sovereign.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Heat-pulse globe.</span> Watch the world wake up as people walk chains.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Voice-clone narration.</span> Midnite reads every chain end-to-end.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Per-paragraph karaoke.</span> The text follows the voice line by line.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Methodology certificates.</span> Earn proof you walked the evidence.</span></li>
            <li className="flex gap-3"><span className="text-[#D4A84A] flex-shrink-0">·</span><span><span className="text-[#D4A84A]">Free while we build.</span> Beta access carries through one week after Grand Opening.</span></li>
          </ul>
        </div>
      </div>
      </div>
    </div>
  );
}
